import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { businessThrowSchema } from '../schema/errorDemoSchema';
import type { BusinessThrowFormValues } from '../schema/errorDemoSchema';
import type { ErrorCodeNode } from '../types/errorDemo';
import { useBusinessThrow } from '../hooks/useThrowDemo';
import { ThrowResultCard } from './ThrowResultCard';

interface BusinessThrowPanelProps {
  selectedNode: ErrorCodeNode | null;
}

/**
 * Panel for POST /api/eraf/error-demo/simulate/business (R8-E-001: was /throw/business).
 * Shown when user selects a COMMON/* or USER/* node from the catalog tree.
 */
export function BusinessThrowPanel({ selectedNode }: BusinessThrowPanelProps) {
  const { t } = useTranslation();
  const { result, throwBusiness, reset } = useBusinessThrow();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<BusinessThrowFormValues>({
    resolver: zodResolver(businessThrowSchema),
    defaultValues: {
      errorCodeName: selectedNode?.enumName ?? '',
      args: selectedNode?.exampleArgs.join(', ') ?? '',
    },
  });

  // Sync form when a new node is selected
  useEffect(() => {
    if (selectedNode) {
      setValue('errorCodeName', selectedNode.enumName);
      setValue('args', selectedNode.exampleArgs.join(', '));
      reset();
    }
  }, [selectedNode, setValue, reset]);

  const onSubmit = async (values: BusinessThrowFormValues) => {
    const argsStr = values.args ?? '';
    const argsArray = argsStr
      ? argsStr.split(',').map((s) => s.trim()).filter(Boolean)
      : [];
    await throwBusiness({
      errorCodeName: values.errorCodeName,
      args: argsArray,
    });
  };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}
      aria-label={t('errorDemo.business.title')}
    >
      {/* Panel header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
          }}
        >
          Business throw
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            padding: '2px var(--spacing-2)',
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-surface-border)',
            color: 'var(--color-text-muted)',
            fontFamily: 'var(--font-family-mono)',
          }}
        >
          POST /simulate/business
        </span>
      </div>

      {/* Selected node info */}
      {selectedNode && (
        <div
          style={{
            padding: 'var(--spacing-3)',
            background: 'var(--color-surface-muted)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--font-size-xs)',
            fontFamily: 'var(--font-family-mono)',
          }}
        >
          <div>
            <span style={{ color: 'var(--color-text-muted)' }}>code: </span>
            <span style={{ color: 'var(--color-brand-primary)', fontWeight: 700 }}>
              {selectedNode.code}
            </span>
          </div>
          <div>
            <span style={{ color: 'var(--color-text-muted)' }}>httpStatus: </span>
            <span style={{ color: 'var(--color-text-secondary)' }}>{selectedNode.httpStatus}</span>
          </div>
          <div>
            <span style={{ color: 'var(--color-text-muted)' }}>message: </span>
            <span style={{ color: 'var(--color-text-secondary)' }}>{selectedNode.messageKo}</span>
          </div>
        </div>
      )}

      {/* Form */}
      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        aria-label={t('errorDemo.business.title')}
      >
        {/* errorCodeName (hidden — set from tree) */}
        <input type="hidden" {...register('errorCodeName')} />

        {/* args field */}
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label
            htmlFor="business-args"
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-1)',
            }}
          >
            {t('errorDemo.business.argsLabel')}
          </label>
          <input
            id="business-args"
            {...register('args')}
            placeholder={t('errorDemo.business.argsPlaceholder')}
            aria-describedby={errors.args ? 'business-args-error' : undefined}
            aria-invalid={!!errors.args}
            style={{
              width: '100%',
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: `1px solid ${errors.args ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-background)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-mono)',
              outline: 'none',
            }}
          />
          {errors.args && (
            <span
              id="business-args-error"
              role="alert"
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-danger)',
                marginTop: 'var(--spacing-1)',
              }}
            >
              {errors.args.message}
            </span>
          )}
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={isSubmitting || result.status === 'loading' || !selectedNode}
          aria-busy={isSubmitting || result.status === 'loading'}
          style={{
            padding: 'var(--spacing-2) var(--spacing-6)',
            background:
              isSubmitting || result.status === 'loading' || !selectedNode
                ? 'var(--color-surface-elevated)'
                : 'var(--color-brand-primary)',
            color:
              isSubmitting || result.status === 'loading' || !selectedNode
                ? 'var(--color-text-muted)'
                : 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            cursor:
              isSubmitting || result.status === 'loading' || !selectedNode
                ? 'not-allowed'
                : 'pointer',
            fontFamily: 'var(--font-family-sans)',
            fontVariantNumeric: 'monospace' as never,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
          }}
        >
          {(isSubmitting || result.status === 'loading') && (
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: 14,
                height: 14,
                border: '2px solid currentColor',
                borderTopColor: 'transparent',
                borderRadius: 'var(--radius-full)',
                animation: 'spin 0.75s linear infinite',
              }}
            />
          )}
          throw
        </button>
      </form>

      {/* Hint */}
      <p
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
          fontStyle: 'italic',
        }}
      >
        {t('errorDemo.business.hint')}
      </p>

      {/* Result card */}
      <ThrowResultCard result={result} />
    </div>
  );
}
